import { Icon } from "@single-js/common/components/icon/icon";
import { renderLoadingError } from "@single-js/common/components/render-loading-error/render-loading-error";
import { FakeLinkCell } from "@single-js/common/components/super-table/super-table";
import { humanizeNodeRole } from "@single-js/common/core/sql/topology/models-topology";
import NumberFormatter from "@single-js/common/util/number-formatter";
import { LONG_EM_DASH } from "@single-js/common/util/symbols";
import classNames from "classnames";
import { formatNodeAddress, getNodeDiskUsage, getNodeMemoryUsage, getNodePair, getPartitionInstanceName, getPartitionInstancePartitionName, getPartitionInstanceType, } from "data/models";
import { PARTITION_INSTANCE_LOG_COLUMN, PARTITION_INSTANCE_ROLE_COLUMN, PARTITION_INSTANCE_STATE_COLUMN, } from "memsql/schema-column-info";
import * as React from "react";
import { titleize } from "underscore.string";
import InternalLink from "view/common/internal-link";
import { formatNodePartitionInstanceStatus, getNodeStateLevel, } from "view/common/models/topology";
import { RatioMetricCell } from "view/common/ratio-metric-cell";
import "./columns-info.scss";
const formatNodeState = (node) => {
    let icon;
    const nodeStateLevel = getNodeStateLevel(node);
    const classes = classNames("node-state-cell", {
        error: nodeStateLevel === "error",
    });
    let state = undefined;
    switch (node.state) {
        case "online":
            icon = "check-circle";
            break;
        case "offline":
            icon = "exclamation-triangle";
            break;
        case "detached":
        case "attaching":
        default:
            icon = "exclamation-circle";
    }
    if (nodeStateLevel === "warning") {
        state = "warn";
    }
    else if (nodeStateLevel === "error" || nodeStateLevel === "success") {
        state = nodeStateLevel;
    }
    return (React.createElement("div", { className: classes },
        React.createElement(Icon, { icon: icon, size: "sm", iconType: "regular", rightMargin: true, state: state }),
        titleize(node.state)));
};
const NODE_STATE_COLUMN = {
    id: "nodeState",
    title: "State",
    formatter: formatNodeState,
    getValue: (node) => node.state,
};
const NODE_PARTITION_INSTANCE_STATUS = {
    id: "nodePartitionInstanceStatus",
    title: "Partition Instance Status",
    defaultMinWidth: 250,
    formatter: (node) => formatNodePartitionInstanceStatus(node),
    getValue: (node) => node.partitionInstanceStatus,
};
const NODE_ROLE_COLUMN = {
    id: "nodeRole",
    title: "Role",
    formatter: (node) => humanizeNodeRole(node.role),
    getValue: (node) => {
        switch (node.role) {
            case "MASTER_AGGREGATOR":
                return 2;
            case "AGGREGATOR":
                return 1;
            case "LEAF":
                return 0;
            default:
                return -1;
        }
    },
};
const NODE_AVAILABILITY_GROUP_COLUMN = {
    id: "nodeAvailabilityGroup",
    title: "Availability Group",
    textAlign: "right",
    formatter: (node) => node.role === "LEAF" ? node.availabilityGroup.toString() : LONG_EM_DASH,
    getValue: (node) => node.role === "LEAF" ? node.availabilityGroup.toNumber() : undefined, // PLAT-3189
};
const NODE_AVAILABILITY_PAIR_COLUMN = {
    id: "nodePair",
    title: "Availability Pair",
    formatter: (node) => {
        const nodePair = getNodePair(node);
        if (nodePair) {
            return (React.createElement(InternalLink, { routeInfo: {
                    name: "cluster.nodes.node",
                    params: {
                        nodeAddress: nodePair,
                    },
                }, category: "schema-explorer", clusterLink: true, onClick: 
                /* stop propagation to the cell click event */ (e) => e.stopPropagation() }, nodePair));
        }
        else {
            return LONG_EM_DASH;
        }
    },
    getValue: (node) => getNodePair(node),
};
const NODE_CONNECTIONS_COLUMN = {
    id: "nodeConnections",
    title: "Open Connections",
    textAlign: "right",
    formatter: (node) => node.openedConnections.toString(),
    // PLAT-3189
    getValue: (node) => node.openedConnections.toNumber(),
};
const NODE_LATENCY_COLUMN = {
    id: "nodeLatency",
    title: "Average Latency",
    textAlign: "right",
    formatter: (node) => node.averageLatency
        ? NumberFormatter.formatDuration(node.averageLatency)
        : "—",
    getValue: (node) => node.averageLatency,
};
const NODE_ADDRESS_COLUMN = {
    id: "nodeHostPort",
    title: "Node Address",
    formatter: (node) => (React.createElement(FakeLinkCell, null, formatNodeAddress(node))),
    // PLAT-3189
    getValue: (node) => node.port.toNumber(),
};
export const NODE_CPU_USAGE_TIP = "Node CPU usage displays the percentage of available CPU being used by the SingleStore DB process on this node and is limited by cgroup configuration.";
export const NODE_CPU_USAGE_COLUMN = {
    id: "nodeTotalCpuUsage",
    title: "CPU Usage",
    sort: "DISABLED",
    description: NODE_CPU_USAGE_TIP,
    formatter: (node) => {
        const renderNodeCpuUsage = (cpuUsage) => (React.createElement(RatioMetricCell, { labelPosition: "top", value: cpuUsage }));
        return renderLoadingError(node.liveMonitoring.totalCpuUsagePercent, renderNodeCpuUsage, { cell: true });
    },
    getValue: (node) => {
        // If loading, return undefined. If error, return null.
        if (node.liveMonitoring.totalCpuUsagePercent.isSuccess()) {
            return node.liveMonitoring.totalCpuUsagePercent.value;
        }
        else if (node.liveMonitoring.totalCpuUsagePercent.isLoading()) {
            return undefined;
        }
        else {
            return null;
        }
    },
};
export const NODE_MEMORY_USAGE_TIP = "Node memory usage displays the percentage of available memory being used by the SingleStore DB process and is limited by cgroup configuration.";
export const NODE_MEMORY_USAGE_COLUMN = {
    id: "nodeMemoryUsage",
    title: "Memory Usage",
    sort: "DISABLED",
    description: NODE_MEMORY_USAGE_TIP,
    formatter: (node) => {
        const renderNodeMemoryUsage = ({ ratio, used, total, }) => {
            const bottomLabel = `${NumberFormatter.formatBytes(used)}/${NumberFormatter.formatBytes(total)}`;
            return (React.createElement(RatioMetricCell, { labelPosition: "top", value: ratio, bottomLabel: bottomLabel }));
        };
        return renderLoadingError(getNodeMemoryUsage(node), renderNodeMemoryUsage, { cell: true });
    },
    getValue: (node) => {
        const nodeMemoryUsage = getNodeMemoryUsage(node);
        // If loading, return undefined. If error, return null.
        if (nodeMemoryUsage.isSuccess()) {
            return nodeMemoryUsage.value.ratio;
        }
        else if (nodeMemoryUsage.isLoading()) {
            return undefined;
        }
        else {
            return null;
        }
    },
};
export const NODE_DISK_USAGE_TIP = "Node disk usage displays the used and available disk space for each mount point being used by this SingleStore DB node.";
export const NODE_DISK_USAGE_COLUMN = {
    id: "nodeDiskUsage",
    title: "Disk Usage",
    sort: "DISABLED",
    description: NODE_DISK_USAGE_TIP,
    formatter: (node) => {
        const renderNodeDiskUsage = ({ ratio, used, total, }) => {
            const bottomLabel = `${NumberFormatter.formatBytes(used)}/${NumberFormatter.formatBytes(total)}`;
            return (React.createElement(RatioMetricCell, { labelPosition: "top", value: ratio, bottomLabel: bottomLabel }));
        };
        return renderLoadingError(getNodeDiskUsage(node), renderNodeDiskUsage, {
            cell: true,
        });
    },
    getValue: (node) => {
        const nodeDiskUsage = getNodeDiskUsage(node);
        // If loading, return undefined. If error, return null.
        if (nodeDiskUsage.isSuccess()) {
            return nodeDiskUsage.value.ratio;
        }
        else if (nodeDiskUsage.isLoading()) {
            return undefined;
        }
        else {
            return null;
        }
    },
};
export function getNodesPageColumns(physicalMonitoringEnabled) {
    if (physicalMonitoringEnabled) {
        return [
            NODE_ADDRESS_COLUMN,
            NODE_STATE_COLUMN,
            NODE_PARTITION_INSTANCE_STATUS,
            NODE_ROLE_COLUMN,
            NODE_CPU_USAGE_COLUMN,
            NODE_MEMORY_USAGE_COLUMN,
            NODE_DISK_USAGE_COLUMN,
        ];
    }
    else {
        return [NODE_ADDRESS_COLUMN, NODE_STATE_COLUMN, NODE_ROLE_COLUMN];
    }
}
export const ALL_NODES_PAGE_COLUMNS = [
    NODE_ADDRESS_COLUMN,
    NODE_STATE_COLUMN,
    NODE_PARTITION_INSTANCE_STATUS,
    NODE_ROLE_COLUMN,
    NODE_CPU_USAGE_COLUMN,
    NODE_MEMORY_USAGE_COLUMN,
    NODE_DISK_USAGE_COLUMN,
    NODE_ADDRESS_COLUMN,
    NODE_STATE_COLUMN,
    NODE_ROLE_COLUMN,
];
export function getHostPageColumns(physicalMonitoringEnabled) {
    if (physicalMonitoringEnabled) {
        return [
            NODE_ADDRESS_COLUMN,
            NODE_STATE_COLUMN,
            NODE_ROLE_COLUMN,
            NODE_CPU_USAGE_COLUMN,
            NODE_MEMORY_USAGE_COLUMN,
            NODE_DISK_USAGE_COLUMN,
        ];
    }
    else {
        return [
            NODE_ADDRESS_COLUMN,
            NODE_STATE_COLUMN,
            NODE_ROLE_COLUMN,
            NODE_AVAILABILITY_GROUP_COLUMN,
            NODE_AVAILABILITY_PAIR_COLUMN,
            NODE_CONNECTIONS_COLUMN,
            NODE_LATENCY_COLUMN,
        ];
    }
}
export const ALL_HOST_PAGE_COLUMNS = [
    NODE_ADDRESS_COLUMN,
    NODE_STATE_COLUMN,
    NODE_ROLE_COLUMN,
    NODE_AVAILABILITY_GROUP_COLUMN,
    NODE_AVAILABILITY_PAIR_COLUMN,
    NODE_CONNECTIONS_COLUMN,
    NODE_LATENCY_COLUMN,
    NODE_CPU_USAGE_COLUMN,
    NODE_MEMORY_USAGE_COLUMN,
    NODE_DISK_USAGE_COLUMN,
];
export const NODE_PARTITION_INSTANCE_COLUMNS = [
    {
        id: "partitionInstancePartitionType",
        title: "Type",
        formatter: getPartitionInstanceType,
        getValue: getPartitionInstanceType,
        defaultMinWidth: 220,
    },
    PARTITION_INSTANCE_STATE_COLUMN,
    {
        id: "partitionInstancePartitionId",
        title: "Partition",
        formatter: (pi) => (React.createElement(InternalLink, { routeInfo: {
                name: "cluster.databases.partition",
                params: {
                    databaseName: pi.databaseName,
                    partitionName: getPartitionInstanceName(pi),
                },
            }, category: "schema-explorer", clusterLink: true, onClick: 
            /* stop propagation to the cell click event */ (e) => e.stopPropagation() }, getPartitionInstancePartitionName(pi))),
        getValue: (pi) => getPartitionInstancePartitionName(pi),
    },
    PARTITION_INSTANCE_ROLE_COLUMN,
    PARTITION_INSTANCE_LOG_COLUMN,
];
